import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { downloadDatasheetStatementPdf, useDatasheetStatement } from "@/lib/datasheets";

const RANGES = [
  { label: "All activity", days: null },
  { label: "Last 30 days", days: 30 },
  { label: "Last 3 months", days: 90 },
  { label: "Last 12 months", days: 365 },
];

export function StatementSection({ datasheetId }: { datasheetId: number }) {
  const [days, setDays] = useState<number | null>(null);
  const from = days === null
    ? undefined
    : new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);

  const { data, isLoading } = useDatasheetStatement(datasheetId, from);
  const columns = (data?.sizes ?? []).filter((s) => s.desc);

  const [downloading, setDownloading] = useState(false);
  async function download() {
    setDownloading(true);
    try {
      await downloadDatasheetStatementPdf(datasheetId, from);
    } catch {
      toast.error("Could not download the statement PDF");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={String(days ?? "")}
          onChange={(e) => setDays(e.target.value === "" ? null : Number(e.target.value))}
          aria-label="Statement period"
        >
          {RANGES.map((r) => (
            <option key={r.label} value={String(r.days ?? "")}>{r.label}</option>
          ))}
        </select>
        <Button size="sm" variant="outline" onClick={() => void download()} disabled={downloading}>
          Download PDF
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}

      {!isLoading && (data?.lines.length ?? 0) === 0 ? (
        <p className="text-sm text-muted-foreground">No activity in this period.</p>
      ) : null}

      {(data?.lines.length ?? 0) > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2">Date</th>
                <th>Type</th>
                <th>Reference</th>
                {columns.map((c) => <th key={c.index} className="text-right">{c.desc}</th>)}
                <th className="text-right">Del.</th>
                <th className="text-right">Fit.</th>
              </tr>
            </thead>
            <tbody>
              {data!.lines.map((l, i) => (
                <tr key={`${l.recordType}-${i}`} className="border-b last:border-0">
                  <td className="py-2">{new Date(l.recordDate).toLocaleDateString()}</td>
                  <td>{l.recordType === "OpeningBalance" ? "Opening" : l.recordType === "Usage" ? "Order" : l.recordType}</td>
                  <td>{l.adjustmentDesc ?? l.reference ?? "—"}</td>
                  {columns.map((c) => (
                    <td key={c.index} className="text-right">{l.sizeBalances[c.index - 1]}</td>
                  ))}
                  <td className="text-right">{l.deliveryBalance}</td>
                  <td className="text-right">{l.fittingBalance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
